import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { AixContentBody } from "@aix/ui"
import { slugToModelId } from "@aix/apiutils/src/models/modelSlugs"
import { z } from "zod"
import { useEffect, useMemo } from "react"
import CustomHead from "~/components/SEOHead/CustomHead"
import AppLayout from "~/layouts/AppLayout"
import { BreadcrumbBanner } from "~/components/BreadcrumbBanner"
import { EffectGallery } from "~/components/VideoEffects/EffectGallery"

const videoEffectsSearchSchema = z.object({
  model: z.string().optional(),
})

function VideoEffectsPage() {
  const breadcrumbItems = [{ label: "Home", href: "/home" }, { label: "Video Effects" }]
  const search = useSearch({ from: "/tools/video-effects" })
  const navigate = useNavigate()

  // Resolve slug to model ID and clean up URL
  const resolvedModel = useMemo(() => {
    if (!search.model) return undefined
    // Try to resolve slug to full model ID
    return slugToModelId(search.model) || search.model
  }, [search.model])

  // Clean up URL after resolving the model
  useEffect(() => {
    if (search.model && resolvedModel) {
      // Remove the model query param from URL (EffectGallery will persist in localStorage)
      void navigate({ to: "/tools/video-effects", search: {}, replace: true })
    }
  }, [search.model, resolvedModel, navigate])

  return (
    <>
      <BreadcrumbBanner items={breadcrumbItems} rightContainerClassName="hidden" />
      <CustomHead
        title="Video Effects | VidMax"
        description="Transform your images into stunning videos with AI-powered effects. Choose from transformation, environmental, action, VFX, style, and camera effects."
        keywords="video effects, AI video, image to video, video transformation, VFX, video generator"
        url="https://vidmax.ai/tools/video-effects"
      />
      <div className="space-y-6">
        <AixContentBody>
          <EffectGallery modelFilter={resolvedModel} />
        </AixContentBody>
      </div>
    </>
  )
}

function VideoEffectsPageWrapper() {
  return (
    <AppLayout>
      <VideoEffectsPage />
    </AppLayout>
  )
}

export const Route = createFileRoute("/tools/video-effects")({
  validateSearch: videoEffectsSearchSchema,
  component: VideoEffectsPageWrapper,
})
